import { useMemo } from 'react';

import { Row, Table } from '@tanstack/react-table';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { parseFormNumber } from '@/helpers/finance';

import { getInvoiceFieldName } from '../constants/generate-invoices-table-columns';
import {
  GenerateInvoiceListItem,
  GenerateInvoiceSubRow,
  GenerateInvoiceSubRowType,
} from '../types';
import { GenerateInvoicesTableFormValues } from '../validations/generate-invoices-validation';
import {
  InvoiceLineValidationInput,
  InvoiceLineValidationInputType,
  InvoiceLineValidationIssueCode,
  InvoiceValidationIssueCode,
  validateInvoiceItems,
  validateInvoiceLine,
} from '../validations/invoice-item-validation';

interface UseInvoicesGenerateDisabledArgs {
  form: UseFormReturn<GenerateInvoicesTableFormValues>;
  table: Table<GenerateInvoiceListItem>;
  selectedRowsCount: number;
}

interface UseInvoicesGenerateDisabledResult {
  isGenerateDisabled: boolean;
  disabledReason: string | undefined;
}

type FormReader = UseFormReturn<GenerateInvoicesTableFormValues>['getValues'];

enum RowInvalidReason {
  ZERO_TOTALS = 'zeroTotals',
  EMPTY_OVERHEAD_NAME = 'emptyOverheadName',
  MISSING_MEMBER = 'missingMember',
  DESCRIPTION_TOO_LONG = 'descriptionTooLong',
  NEGATIVE_TOTAL = 'negativeTotalPerEntity',
}

// Field names are keyed by entity id, so a row reads its own leaf directly — no need to map
// its position among same-typed siblings back to an array index.
const readField = (getValues: FormReader, row: Row<GenerateInvoiceListItem>, name: string) =>
  getValues(getInvoiceFieldName(row, name) as never) as unknown;

const getRowValidationInput = (
  rowObject: Row<GenerateInvoiceListItem>,
  getValues: FormReader
): InvoiceLineValidationInput | null => {
  const subRow = rowObject.original as unknown as GenerateInvoiceSubRow;

  if (subRow.type === GenerateInvoiceSubRowType.MEMBER) {
    return {
      type: InvoiceLineValidationInputType.Member,
      quantity: parseFormNumber(readField(getValues, rowObject, 'hours') ?? subRow.hours),
      unitPrice: parseFormNumber(
        readField(getValues, rowObject, 'effectiveRate') ?? subRow.effectiveRate
      ),
    };
  }

  if (subRow.type === GenerateInvoiceSubRowType.ADDED_MEMBER) {
    const memberId = readField(getValues, rowObject, 'memberId') as number | null | undefined;
    return {
      type: InvoiceLineValidationInputType.Member,
      memberId: memberId ?? null,
      quantity: parseFormNumber(readField(getValues, rowObject, 'hours') ?? 0),
      unitPrice: parseFormNumber(readField(getValues, rowObject, 'effectiveRate') ?? 0),
    };
  }

  if (subRow.type === GenerateInvoiceSubRowType.OVERHEAD) {
    return {
      type: InvoiceLineValidationInputType.Overhead,
      description: String(readField(getValues, rowObject, 'description') ?? subRow.description),
      unitPrice: parseFormNumber(readField(getValues, rowObject, 'effectiveRate') ?? 0),
    };
  }

  return null;
};

const getRowInvalidReason = (
  rowObject: Row<GenerateInvoiceListItem>,
  getValues: FormReader
): RowInvalidReason | null => {
  const input = getRowValidationInput(rowObject, getValues);
  if (!input) return null;

  const issueCodes = validateInvoiceLine(input).map(issue => issue.code);
  if (issueCodes.includes(InvoiceLineValidationIssueCode.MissingMember)) {
    return RowInvalidReason.MISSING_MEMBER;
  }
  if (issueCodes.includes(InvoiceLineValidationIssueCode.ZeroMemberLine)) {
    return RowInvalidReason.ZERO_TOTALS;
  }
  if (issueCodes.includes(InvoiceLineValidationIssueCode.EmptyDescription)) {
    return RowInvalidReason.EMPTY_OVERHEAD_NAME;
  }
  if (issueCodes.includes(InvoiceLineValidationIssueCode.DescriptionTooLong)) {
    return RowInvalidReason.DESCRIPTION_TOO_LONG;
  }

  return null;
};

const hasNegativeTotalPerProject = (
  selectedRowObjects: Row<GenerateInvoiceListItem>[],
  getValues: FormReader
): boolean => {
  const itemsByParent = new Map<string, InvoiceLineValidationInput[]>();
  for (const rowObject of selectedRowObjects) {
    const parentRow = rowObject.getParentRow();
    if (!parentRow) continue;
    const input = getRowValidationInput(rowObject, getValues);
    if (!input) continue;
    itemsByParent.set(parentRow.id, [...(itemsByParent.get(parentRow.id) ?? []), input]);
  }
  return [...itemsByParent.values()].some(items =>
    validateInvoiceItems(items).includes(InvoiceValidationIssueCode.NegativeTotal)
  );
};

const REASON_TO_TOOLTIP: Record<RowInvalidReason, string> = {
  [RowInvalidReason.ZERO_TOTALS]: 'common:generateEntities.disabledTooltipZeroValues',
  [RowInvalidReason.EMPTY_OVERHEAD_NAME]:
    'common:generateEntities.disabledTooltipEmptyOverheadName',
  [RowInvalidReason.MISSING_MEMBER]: 'common:generateEntities.disabledTooltipMissingMember',
  [RowInvalidReason.DESCRIPTION_TOO_LONG]:
    'authenticated.invoices:details.edit.validation.descriptionTooLong',
  [RowInvalidReason.NEGATIVE_TOTAL]: 'common:generateEntities.disabledTooltipNegativeTotalInvoices',
};

export const useInvoicesGenerateDisabled = ({
  form,
  table,
  selectedRowsCount,
}: UseInvoicesGenerateDisabledArgs): UseInvoicesGenerateDisabledResult => {
  const { t } = useTranslation(['common', 'authenticated.invoices']);
  // Watch the whole form so this recomputes on any edit; leaves are read by id path.
  const watchedProject = useWatch({ control: form.control, name: 'project' });
  const selectedRowObjects = table.getSelectedRowModel().flatRows;

  const invalidReason = useMemo<RowInvalidReason | null>(() => {
    if (!selectedRowsCount) return null;
    const { getValues } = form;
    for (const rowObject of selectedRowObjects) {
      const reason = getRowInvalidReason(rowObject, getValues);
      if (reason) return reason;
    }
    if (hasNegativeTotalPerProject(selectedRowObjects, getValues)) {
      return RowInvalidReason.NEGATIVE_TOTAL;
    }
    return null;
    // `watchedProject` drives recomputation; `form.getValues` reads the latest leaf values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRowsCount, selectedRowObjects, watchedProject]);

  const isGenerateDisabled = selectedRowsCount === 0 || invalidReason !== null;

  const disabledReason = useMemo(() => {
    if (selectedRowsCount === 0) return t('common:generateEntities.disabledTooltipNoSelection');
    if (invalidReason) return t(REASON_TO_TOOLTIP[invalidReason]);
    return undefined;
  }, [selectedRowsCount, invalidReason, t]);

  return { isGenerateDisabled, disabledReason };
};
